import { useEffect, useRef, useState } from 'react';
import { submitEnquiry, fetchAdmissionClasses } from '../lib/api.js';
import { useApi } from '../hooks/useApi.js';

// Classes come from the admin panel (Settings → Admission classes), where each
// class also gets its own notification email. This list is only used if the
// API call fails — the server matches these by name.
const FALLBACK_CLASSES = [
  'Play Group', 'Nursery', 'KG I', 'KG II',
  'Class I', 'Class II', 'Class III', 'Class IV', 'Class V',
  'Class VI', 'Class VII', 'Class VIII', 'Class IX', 'Class X',
  'Class XI', 'Class XII',
].map((name) => ({ id: name, name }));

const EMPTY = {
  parentName: '', phone: '', email: '', childAge: '', applyingFor: '', message: '',
};

export function EnquiryForm() {
  const { data: classData, error: classError } = useApi(fetchAdmissionClasses, []);
  const classes = Array.isArray(classData) && classData.length ? classData : FALLBACK_CLASSES;
  const usingFallback = classes === FALLBACK_CLASSES;
  const classesReady = classData !== null || classError !== null;

  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | sending | ok | error
  const [error, setError] = useState(null);
  // What was just sent — shown back to the parent in the confirmation panel.
  const [sent, setSent] = useState(null);
  const doneRef = useRef(null);

  // Bring the confirmation into view and move focus to it (screen readers
  // announce it, keyboard users land on it instead of a vanished button).
  useEffect(() => {
    if (status !== 'ok' || !doneRef.current) return;
    doneRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    doneRef.current.focus({ preventScroll: true });
  }, [status]);

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError(null);
    try {
      // `applyingFor` holds the selected class's id (or its name in fallback mode).
      const { applyingFor, ...rest } = form;
      const res = await submitEnquiry(usingFallback
        ? { ...rest, applyingFor }
        : { ...rest, admissionClassId: applyingFor });
      setSent({
        id: res?.id,
        parentName: form.parentName.trim(),
        className: classes.find((c) => String(c.id) === String(applyingFor))?.name,
        phone: form.phone.trim(),
        email: form.email.trim(),
      });
      setStatus('ok');
      setForm(EMPTY);
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err.message || 'Submission failed';
      setError(msg);
      setStatus('error');
    }
  };

  if (status === 'ok' && sent) {
    return (
      <div className="enquiry-done" role="status" aria-live="polite" tabIndex={-1} ref={doneRef}>
        <span className="enquiry-done-icon" aria-hidden="true">✓</span>
        <div>
          <h3>Thank you{sent.parentName ? `, ${sent.parentName}` : ''} — your enquiry has been received.</h3>
          <p>
            We&apos;ve passed your admission enquiry
            {sent.className ? <> for <strong>{sent.className}</strong></> : null} to our admission team.
            They will contact you on <strong>{sent.phone}</strong>
            {sent.email ? <> or at <strong>{sent.email}</strong></> : null} within 1–2 working days.
          </p>
          {sent.id ? <p className="enquiry-done-ref">Reference no. MLIS-ENQ-{sent.id}</p> : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => { setStatus('idle'); setSent(null); }}
          >
            Submit another enquiry
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="form-grid" style={{ maxWidth: 760 }} onSubmit={onSubmit}>
      <div className="field">
        <label htmlFor="ef-parentName">Parent / guardian name</label>
        <input id="ef-parentName" type="text" required value={form.parentName} onChange={change('parentName')} disabled={status === 'sending'} />
      </div>
      <div className="field">
        <label htmlFor="ef-phone">Phone number</label>
        <input id="ef-phone" type="tel" required value={form.phone} onChange={change('phone')} disabled={status === 'sending'} />
      </div>
      <div className="field">
        <label htmlFor="ef-email">Email address</label>
        <input id="ef-email" type="email" value={form.email} onChange={change('email')} disabled={status === 'sending'} />
      </div>
      <div className="field">
        <label htmlFor="ef-childAge">Child's age</label>
        <input id="ef-childAge" type="text" placeholder="e.g. 5" value={form.childAge} onChange={change('childAge')} disabled={status === 'sending'} />
      </div>
      <div className="field full">
        <label htmlFor="ef-applyingFor">Applying for class</label>
        <select
          id="ef-applyingFor"
          required
          value={form.applyingFor}
          onChange={change('applyingFor')}
          disabled={status === 'sending' || !classesReady}
        >
          <option value="">{classesReady ? '— select —' : 'Loading classes…'}</option>
          {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="field full">
        <label htmlFor="ef-message">Message</label>
        <textarea
          id="ef-message"
          placeholder="Tell us a little about your child"
          rows={4}
          value={form.message}
          onChange={change('message')}
          disabled={status === 'sending'}
        />
      </div>
      <div className="full" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="submit" className="btn btn-primary" disabled={status === 'sending'}>
          {status === 'sending' ? 'Sending…' : 'Submit enquiry'}
        </button>
        {status === 'error' && <span style={{ color: '#b54033', fontSize: 14 }}>{error}</span>}
      </div>
    </form>
  );
}
